// A small, consistent badge for a provider we recognise (mail, sending, DNS, hosting,
// registrar, certificate issuer). Seeing the same familiar badges every time makes an
// unfamiliar one stand out — that's the point.
//
// Where the brand allows it we show its mark from Simple Icons (CC0 files; the marks
// stay their owners' trademarks, used here only to say which service it is). Brands
// that have asked Simple Icons NOT to carry their logo (Microsoft, Amazon/AWS,
// Salesforce, Twilio/SendGrid, Proofpoint, Mimecast…) get initials on their colour
// instead — never their logo. Unrecognised providers get a plain grey "?" on purpose.
//
// The marks come from providerIcons.js, generated from simple-icons (scripts/build-provider-icons.mjs).

import { ICONS } from './providerIcons.js';

// name → a Simple Icons icon, or { initials, hex } for brands without a usable mark
const BRANDS = {
  'Google Workspace': ICONS.google,
  'Google Cloud': ICONS.googlecloud,
  'Google Cloud DNS': ICONS.googlecloud,
  Gmail: ICONS.gmail,
  Mailchimp: ICONS.mailchimp,
  HubSpot: ICONS.hubspot,
  Mailgun: ICONS.mailgun,
  'Brevo (Sendinblue)': ICONS.brevo,
  SparkPost: ICONS.sparkpost,
  'Campaign Monitor': ICONS.campaignmonitor,
  Zendesk: ICONS.zendesk,
  Intercom: ICONS.intercom,
  Zoho: ICONS.zoho,
  'Zoho Mail': ICONS.zoho,
  'Proton Mail': ICONS.protonmail,
  'Cisco Secure Email': ICONS.cisco,
  Cloudflare: ICONS.cloudflare,
  GoDaddy: ICONS.godaddy,
  Namecheap: ICONS.namecheap,
  'Namecheap Private Email': ICONS.namecheap,
  IONOS: ICONS.ionos,
  OVHcloud: ICONS.ovh,
  DigitalOcean: ICONS.digitalocean,
  Hetzner: ICONS.hetzner,
  Fastly: ICONS.fastly,
  Akamai: ICONS.akamai,
  Vercel: ICONS.vercel,
  Netlify: ICONS.netlify,
  'Automattic (WordPress.com)': ICONS.wordpress,
  Squarespace: ICONS.squarespace,
  Wix: ICONS.wix,
  Shopify: ICONS.shopify,
  Gandi: ICONS.gandi,
  "Let's Encrypt": ICONS.letsencrypt,
  // No usable mark (their owners declined): initials on the brand's own colour
  'Microsoft 365': { initials: '365', hex: '0078D4' },
  'Microsoft Azure': { initials: 'Az', hex: '0078D4' },
  'Azure DNS': { initials: 'Az', hex: '0078D4' },
  'Microsoft Dynamics': { initials: 'Dyn', hex: '0078D4' },
  'Amazon SES': { initials: 'SES', hex: 'FF9900' },
  'Amazon Route 53': { initials: 'R53', hex: 'FF9900' },
  'Amazon Web Services': { initials: 'AWS', hex: 'FF9900' },
  'Amazon WorkMail': { initials: 'AWS', hex: 'FF9900' },
  Salesforce: { initials: 'SF', hex: '00A1E0' },
  Pardot: { initials: 'SF', hex: '00A1E0' },
  SendGrid: { initials: 'SG', hex: '1A82E2' },
  Proofpoint: { initials: 'PP', hex: '1A1A1A' },
  Mimecast: { initials: 'Mc', hex: '0E2A47' },
  Barracuda: { initials: 'Bc', hex: '0050A0' },
  Postmark: { initials: 'Pm', hex: 'FFDE00' },
  Klaviyo: { initials: 'Kl', hex: '232426' },
  Marketo: { initials: 'Mk', hex: '5C4C9F' },
  ActiveCampaign: { initials: 'AC', hex: '004CFF' },
  'Constant Contact': { initials: 'CC', hex: '1856ED' },
  Cvent: { initials: 'Cv', hex: '006AE1' },
  Eventbrite: { initials: 'Eb', hex: 'F05537' },
  Bizzabo: { initials: 'Bz', hex: '4A3AFF' },
  Swoogo: { initials: 'Sw', hex: '2A4A9E' },
  Freshdesk: { initials: 'Fd', hex: '25C16F' },
  Fastmail: { initials: 'Fm', hex: '0067B9' },
  Rackspace: { initials: 'Rs', hex: 'C40022' },
  '123 Reg': { initials: '123', hex: '00A9E0' },
  DNSimple: { initials: 'DS', hex: '1A73E8' },
  NS1: { initials: 'NS1', hex: '6A3BD1' },
  Hover: { initials: 'Hv', hex: '2C3E50' },
  'WP Engine': { initials: 'WP', hex: '0ECAD4' },
  'Linode / Akamai': ICONS.akamai,
};

// Registrars and certificate issuers arrive as company names ("GoDaddy.com, LLC")
const BY_PATTERN = [
  [/godaddy/i, 'GoDaddy'], [/namecheap/i, 'Namecheap'], [/cloudflare/i, 'Cloudflare'], [/google/i, 'Google Cloud'],
  [/ionos|1&1/i, 'IONOS'], [/\bovh/i, 'OVHcloud'], [/gandi/i, 'Gandi'], [/amazon/i, 'Amazon Web Services'],
  [/microsoft|azure/i, 'Microsoft Azure'], [/123-?reg/i, '123 Reg'], [/squarespace/i, 'Squarespace'], [/wix/i, 'Wix'],
  [/let'?s ?encrypt/i, "Let's Encrypt"], [/hover/i, 'Hover'], [/digicert/i, 'DigiCert'], [/sectigo/i, 'Sectigo'],
];
BRANDS.DigiCert = { initials: 'DC', hex: '0174C3' };
BRANDS.Sectigo = { initials: 'Sg', hex: '1F5F3A' };

export function brandFor(name) {
  if (!name) return null;
  if (BRANDS[name]) return { name, ...BRANDS[name] };
  const hit = BY_PATTERN.find(([re]) => re.test(name));
  return hit ? { name: hit[1], ...BRANDS[hit[1]] } : null;
}

/** Black or white, whichever reads better on this colour. */
function inkOn(hex) {
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? '#001D4D' : '#FFFFFF';
}

/**
 * <ProviderLogo name="Mailchimp" />  → the badge, with the name for screen readers.
 * size: px (default 22). Unknown names get a neutral "?" tile.
 */
export default function ProviderLogo({ name, size = 28 }) {
  const brand = brandFor(name);
  const style = { width: size, height: size };
  if (!brand) {
    return <span className="provider-logo provider-logo-unknown" style={style} title="Not a provider we recognise" aria-hidden="true">?</span>;
  }
  const ink = inkOn(brand.hex);
  if (brand.path) {
    return (
      <span className="provider-logo" style={{ ...style, background: `#${brand.hex}` }} title={brand.name} aria-hidden="true">
        <svg viewBox="0 0 24 24" width={size * 0.62} height={size * 0.62} fill={ink} focusable="false"><path d={brand.path} /></svg>
      </span>
    );
  }
  return (
    <span className="provider-logo provider-logo-initials" title={brand.name} aria-hidden="true"
      style={{ ...style, background: `#${brand.hex}`, color: ink, fontSize: Math.max(9, Math.round(size * (brand.initials.length > 2 ? 0.36 : 0.44))) }}>
      {brand.initials}
    </span>
  );
}
